import { useState } from 'react';
import emailjs from '@emailjs/browser';
import {
  ArrowLeft,
  Lock,
  ShieldCheck,
  MapPin,
  Phone,
  User,
  MessageSquare,
  Send,
  CheckCircle2,
  Copy,
  Check,
} from 'lucide-react';
import branchesData from '../data/branchesData';
import emailConfig from '../config/emailConfig';

function GstRow({ branch, revealed }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(branch.gst).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="bg-gradient-to-br from-blue-300 to-black w-12 h-12 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
            <MapPin className="h-6 w-6 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 text-lg">{branch.name}</h4>
            <p className="text-gray-500 text-sm">{branch.address}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-2 text-gray-600 text-sm mb-4">
        <Phone className="h-4 w-4 text-blue-900" />
        <span>{branch.phone}</span>
      </div>

      <div
        className={`rounded-xl p-4 flex items-center justify-between transition-all duration-500 ${
          revealed ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
        }`}
      >
        {revealed ? (
          <>
            <div>
              <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">
                GST Number
              </div>
              <div className="font-mono font-bold text-gray-900 text-lg tracking-wide">
                {branch.gst}
              </div>
            </div>
            <button
              onClick={handleCopy}
              className="bg-white border border-green-300 hover:bg-green-100 transition-colors p-2.5 rounded-lg"
              title="Copy GST number"
            >
              {copied ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : (
                <Copy className="h-5 w-5 text-green-700" />
              )}
            </button>
          </>
        ) : (
          <>
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                GST Number
              </div>
              <div className="font-mono font-bold text-gray-300 text-lg tracking-widest select-none">
                •••• •••• ••••
              </div>
            </div>
            <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg border border-gray-200">
              <Lock className="h-4 w-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-400">Locked</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EnquiryPage({ goHome }) {
  const [formData, setFormData] = useState({ name: '', contact: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [revealed, setRevealed] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Please enter your name';
    if (!formData.contact.trim()) newErrors.contact = 'Please enter your phone or email';
    if (!formData.message.trim()) newErrors.message = 'Please enter your enquiry';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validate()) return;
  
    setStatus('sending');
  
    const branchInfo = branchesData
      .map((b) => `${b.name}: ${b.address} | ${b.phone}`)
      .join('\n');
  
    const templateParams = {
      from_name: formData.name,
      from_contact: formData.contact,
      message: formData.message,
      branch_info: branchInfo,
    };
  
    try {
      console.log('Sending enquiry...', templateParams);
      

      const response = await emailjs.send(
        emailConfig.SERVICE_ID,
        emailConfig.TEMPLATE_ID,
        templateParams,
        {
          publicKey: emailConfig.PUBLIC_KEY,
        }
      );
  
      console.log('EMAIL SUCCESS:', response.status, response.text);
  
      setStatus('success');
      setRevealed(true);
    } catch (err) {
      console.error('EMAIL FAILED:', err);
      console.error('EmailJS status:', err?.status);
      console.error('EmailJS message:', err?.text);
    
      setStatus('error');
    }
    
  };
  

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/40 to-white pt-28 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={goHome}
          className="inline-flex items-center space-x-2 text-blue-900 font-semibold mb-8 hover:-translate-x-1 transition-transform"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Home</span>
        </button>

        <div className="text-center mb-14 animate-fadeInUp">
          <div className="inline-flex items-center space-x-2 bg-blue-100 px-4 py-2 rounded-full mb-4">
            <ShieldCheck className="h-4 w-4 text-blue-900" />
            <span className="text-sm font-semibold text-blue-900">Enquiry & GST Details</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Send Us an Enquiry</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Fill the form below and our team will get back to you shortly. GST details for all our
            branches are unlocked automatically once you submit your enquiry.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Form */}
          <div className="lg:col-span-3 animate-fadeInUp" style={{ animationDelay: '100ms' }}>
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8 md:p-10">
              {status === 'success' ? (
                <div className="text-center py-10">
                  <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Thank you, {formData.name}!</h3>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                    Your enquiry has been sent to our team. We'll reach out to you at{' '}
                    <span className="font-semibold text-gray-900">{formData.contact}</span> shortly.
                    GST details for all branches are now visible alongside this form.
                  </p>
                  <button
                    onClick={() => {
                      setStatus('idle');
                      setFormData({ name: '', contact: '', message: '' });
                    }}
                    className="text-blue-900 font-semibold hover:underline"
                  >
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Enquiry Form</h3>
                  <p className="text-gray-500 mb-6">All fields are required</p>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g. Rohan Sharma"
                        className={`w-full pl-12 pr-4 py-3.5 rounded-xl border ${
                          errors.name ? 'border-red-400' : 'border-gray-200'
                        } focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-gray-900`}
                      />
                    </div>
                    {errors.name && <p className="text-red-500 text-sm mt-1.5">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone or Email
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        name="contact"
                        value={formData.contact}
                        onChange={handleChange}
                        placeholder="e.g. 98765 43210 or you@email.com"
                        className={`w-full pl-12 pr-4 py-3.5 rounded-xl border ${
                          errors.contact ? 'border-red-400' : 'border-gray-200'
                        } focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-gray-900`}
                      />
                    </div>
                    {errors.contact && <p className="text-red-500 text-sm mt-1.5">{errors.contact}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Enquiry
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-4 top-3.5 h-5 w-5 text-gray-400" />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        placeholder="Tell us what you're looking for..."
                        className={`w-full pl-12 pr-4 py-3.5 rounded-xl border ${
                          errors.message ? 'border-red-400' : 'border-gray-200'
                        } focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-gray-900 resize-none`}
                      />
                    </div>
                    {errors.message && <p className="text-red-500 text-sm mt-1.5">{errors.message}</p>}
                  </div>

                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-4">
                      Something went wrong sending your enquiry. Please try again, or call us directly
                      at {branchesData[0].phone}.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="group w-full bg-gradient-to-r from-blue-300 to-black text-white px-8 py-4 rounded-xl hover:shadow-xl transition-all transform hover:scale-[1.02] font-semibold text-lg flex items-center justify-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    <span>{status === 'sending' ? 'Sending...' : 'Submit Enquiry'}</span>
                    {status !== 'sending' && (
                      <Send className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* GST / Branches panel */}
          <div className="lg:col-span-2 space-y-6 animate-fadeInUp" style={{ animationDelay: '200ms' }}>
            <div
              className={`rounded-2xl p-6 border ${
                revealed
                  ? 'bg-green-50 border-green-200'
                  : 'bg-blue-50 border-blue-200'
              }`}
            >
              <div className="flex items-center space-x-3">
                {revealed ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                ) : (
                  <Lock className="h-6 w-6 text-blue-900 flex-shrink-0" />
                )}
                <p className={`text-sm font-medium ${revealed ? 'text-green-800' : 'text-blue-900'}`}>
                  {revealed
                    ? 'GST numbers unlocked — thank you for your enquiry!'
                    : 'We keep verified GST numbers for every branch. Submit the enquiry form to unlock them here.'}
                </p>
              </div>
            </div>

            {branchesData.map((branch) => (
              <GstRow key={branch.id} branch={branch} revealed={revealed} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EnquiryPage;
